import { CaseStudyPage, type CaseStudyData } from "@/components/CaseStudyTemplate";
import { Mail, MessageSquare, Globe, MapPin, Megaphone, Database } from "lucide-react";

const data: CaseStudyData = {
  seoTitle: "Centennial Family Foods Case Study | Paper Flyers to Digital | Frayze",
  seoDescription:
    "See how Frayze turned a Thunder Bay grocer's paper flyer into a multi-channel system: new website, weekly email + SMS deals, local SEO, ads, and an owned database.",
  seoKeywords:
    "grocery store marketing Thunder Bay, independent grocer digital transformation, digital flyer email SMS, weekly flyer email, local SEO grocery, Centennial Family Foods case study",
  canonical: "https://frayze.ca/case-studies/centennial-family-foods-retail-system/",
  breadcrumbs: [
    { name: "Home", item: "https://frayze.ca/" },
    { name: "Case Studies", item: "https://frayze.ca/case-studies/" },
    { name: "Centennial Family Foods", item: "https://frayze.ca/case-studies/centennial-family-foods-retail-system/" },
  ],

  industry: "Independent Grocery",
  industryColor: "orange",
  clientName: "Centennial Family Foods",
  headline: (
    <>
      Replacing <span className="text-orange-400">paper flyers</span> with a <span className="text-orange-400">multi-channel retail system</span> for a Thunder Bay grocer
    </>
  ),
  stats: [
    { value: "100%", label: "Paper Flyer Replaced" },
    { value: "5+", label: "Channels Activated" },
    { value: "Weekly", label: "Deal Distribution Cadence" },
    { value: "Local", label: "SEO Foundation" },
  ],

  client: "Centennial Family Foods",
  industryLabel: "Independent Grocery / Retail",
  location: "Thunder Bay, Ontario",
  services:
    "Website, Digital Flyer System, Email + SMS Distribution, Database Management, Local SEO, SEO, Ad Management, Social Media Management",
  website: "centennialfamilyfoods.ca",
  sidebarCTA: { label: "Visit Centennial Family Foods", href: "https://centennialfamilyfoods.ca" },

  sections: [
    {
      title: "The Challenge",
      content:
        "Centennial Family Foods is a long-standing independent grocer in Thunder Bay. Their business ran on the same playbook most independent grocers in Northern Ontario rely on: a weekly paper flyer dropped through letterboxes and stacked at the door. The flyer worked, but it had a ceiling no one was talking about.",
      bullets: [
        "<strong>Paper flyer reach was capped:</strong> distribution was geographic, not behavioural (a one-shot drop that hit every household once, regardless of whether they were a regular shopper or a passer-by).",
        "<strong>Zero feedback loop:</strong> no way to know who opened the flyer, who acted on a deal, or which items drove the most foot traffic.",
        "<strong>Rising print + distribution costs:</strong> ink, paper, and door-to-door drop costs were climbing every quarter, and inflation was making weekly flyers a real line-item question.",
        "<strong>No owned audience:</strong> Centennial didn't have a database of their own shoppers. Every deal was an anonymous broadcast.",
        "<strong>Digital presence was thin:</strong> the website was outdated, local search visibility was weak, and social media was inconsistent.",
      ],
    },
    {
      title: "The System We Built",
      content:
        "Frayze didn't replace the flyer. We replaced the entire distribution model around it. The weekly deals stayed (they're the heartbeat of the business), but the flyer became one output of a connected retail system, not the whole strategy.",
    },
    {
      title: "1. New Website Built for Weekly Deals",
      content:
        "We rebuilt the Centennial Family Foods website from the ground up (mobile-first, fast, and structured around the one thing shoppers actually come for): this week's deals. Every page is optimized to surface the current flyer, store hours, location, and the call-to-action that matters most for an independent grocer: get them in the door.",
    },
    {
      title: "2. Digital Flyer + Email & SMS Distribution",
      content:
        "The paper flyer was reborn as a digital asset distributed every week via email and SMS to an owned subscriber list. Customers opt in once, then get the flyer the moment it goes live (faster than the print version ever could, and trackable end-to-end). Open rates, click-throughs on featured items, and SMS engagement all feed back into next week's flyer design.",
    },
    {
      title: "3. Database Management: Centennial's Owned Audience",
      content:
        "We built the database Centennial never had. Every email and SMS subscriber, every web form, every loyalty signup feeds one source-of-truth list, segmented by behaviour and geography. The database is now the most valuable marketing asset Centennial owns, and unlike rented audiences on social platforms, it belongs to them.",
    },
    {
      title: "4. Local SEO + SEO",
      content:
        "We rebuilt Centennial's Google Business Profile, structured the website for local search (schema, weekly deal markup, location-specific landing pages), and built citations across the directories that matter for an independent grocer in Northern Ontario. The goal: when someone in Thunder Bay searches for groceries, deals, or specific weekly specials, Centennial shows up first.",
    },
    {
      title: "5. Ad Management + Social Media",
      content:
        "We took over the paid and organic social side of the business: Meta and Google ads targeted to Centennial's actual catchment area, retargeting flyer subscribers who haven't visited the store in 30+ days, and a consistent social posting cadence that keeps the brand visible between flyers. Every dollar of ad spend is tied back to a measurable outcome, not just impressions.",
    },
  ],

  results: [
    { icon: Mail, label: "Owned Email Audience", sublabel: "Subscriber list grown from zero; weekly flyer delivered directly" },
    { icon: MessageSquare, label: "SMS Distribution Live", sublabel: "Weekly deals reach customers' phones, faster than paper ever could" },
    { icon: Globe, label: "New Website + CRM", sublabel: "Mobile-first site built around weekly deals; database wired in" },
    { icon: MapPin, label: "Local SEO Build-Out", sublabel: "GBP, schema, citations, location pages (built for Thunder Bay searches)" },
    { icon: Megaphone, label: "Ads + Social Managed", sublabel: "Meta + Google ads, retargeting, organic social (all on one calendar)" },
    { icon: Database, label: "First-Party Database", sublabel: "Centennial now owns the audience instead of renting it" },
  ],

  testimonial: undefined, // ready for a real quote when one is captured

  stack: [
    "Custom Website",
    "Email Distribution (Weekly Flyer)",
    "SMS Distribution",
    "Database / CRM",
    "Google Business Profile",
    "Local SEO + Schema",
    "Meta Ads",
    "Google Ads",
    "Social Media Management",
  ],
};

const CentennialFamilyFoods = () => <CaseStudyPage data={data} />;
export default CentennialFamilyFoods;
